// src/components/crm/FunnelManager.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Button,
  TextInput,
  Modal,
  Select,
  Badge,
  Spinner,
} from "flowbite-react";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  HiOutlinePencil,
  HiOutlinePlus,
  HiOutlineBars3,
  HiTrash,
  HiOutlineFunnel,
  HiStar,
  HiOutlineStar,
  HiCheckCircle,
  HiXCircle,
} from "react-icons/hi2";
import axios from "../../config/axiosInstance";
import { CRM, USERS } from "../../config/apiConfig";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useApplication } from "../../contexts/ApplicationContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ReactSelect from "react-select";

/* ========= helpers ========= */

const makeClientKey = () =>
  `stage_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const withClientKeys = (stages = []) =>
  stages.map((s, i) => ({
    // preserva tudo que já existe
    ...s,
    // se vier do servidor sem clientKey, cria um
    clientKey: s.clientKey || makeClientKey(),
    order: s.order ?? i + 1,
  }));

const toArray = (maybe) => {
  if (Array.isArray(maybe)) return maybe;
  if (maybe?.rows && Array.isArray(maybe.rows)) return maybe.rows;
  if (maybe?.items && Array.isArray(maybe.items)) return maybe.items;
  if (maybe?.data && Array.isArray(maybe.data)) return maybe.data;
  return [];
};

const normalizeId = (value) => (value == null ? null : String(value));
const normalizeCategory = (value) => String(value || "").trim();
const normalizeIdList = (values) => {
  if (!values) return [];
  if (Array.isArray(values)) return values.map(normalizeId).filter(Boolean);
  if (typeof values === "string") {
    return values
      .split(",")
      .map((item) => normalizeId(item))
      .filter(Boolean);
  }
  return [];
};

const getUserReferenceId = (user) =>
  user?.id ?? user?.userId ?? user?._id ?? user?.user_id ?? user?.UserId;

const gatherFunnelVisibleUserIds = (funnel) => {
  if (!funnel) return [];
  if (Array.isArray(funnel.visibleUserIds) && funnel.visibleUserIds.length)
    return funnel.visibleUserIds;
  if (Array.isArray(funnel.allowedUserIds) && funnel.allowedUserIds.length)
    return funnel.allowedUserIds;
  if (Array.isArray(funnel.ownerUserIds) && funnel.ownerUserIds.length)
    return funnel.ownerUserIds;
  if (funnel.ownerUserId) return [funnel.ownerUserId];
  return [];
};

const reactSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderColor: state.isFocused ? "var(--primary)" : "#d1d5db",
    boxShadow: "none",
    borderRadius: 10,
    ":hover": {
      borderColor: state.isFocused ? "var(--primary)" : "#a855f7",
    },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  multiValue: (base) => ({ ...base, backgroundColor: "#eef2ff" }),
  multiValueLabel: (base) => ({ ...base, color: "#5b21b6" }),
  multiValueRemove: (base) => ({ ...base, color: "#7c3aed" }),
};

const VISIBILITY = [
  { value: "PRIVATE", label: "Privado" },
  { value: "PUBLIC", label: "Todos os usuários" },
];

const AUTO_LEAD_STATUS = [
  { value: "", label: "— Não alterar —" },
  { value: "working", label: "Working" },
  { value: "qualified", label: "Qualified" },
  { value: "unqualified", label: "Unqualified" },
  { value: "converted", label: "Converted" },
];

/* ========= API ========= */
async function getFunnels() {
  const res = await axios.get(CRM.FUNNELS);
  return toArray(res?.data?.data ?? res?.data);
}

async function getUsersList() {
  const res = await axios.get(`${USERS.GET_USERS}?itemsPerPage=-1`);
  const payload = res?.data?.data?.data ?? res?.data?.data ?? res?.data;
  return toArray(payload);
}

async function saveFunnel(payload) {
  const hasId = !!payload.id && String(payload.id).trim() !== "";
  const url = hasId ? `${CRM.FUNNELS}/${payload.id}` : CRM.FUNNELS;
  const method = hasId ? "put" : "post";
  const res = await axios[method](url, payload);
  return res.data;
}
async function deleteFunnel(id) {
  const res = await axios.delete(`${CRM.FUNNELS}/${id}`);
  return res.data;
}

/* ============================ Stage UI (master–detail) ============================ */
/** Linha minimalista (sortable) */
function SortableStageRow({
  id,
  stage,
  isClosing,
  onRename,
  onMarkClosing,
  onDelete,
  onOpenInspector,
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  const [editing, setEditing] = useState(false);
  const [localName, setLocalName] = useState(stage?.name || "");
  const commit = () => {
    const trimmed = localName.trim();
    if (trimmed && trimmed !== stage.name) onRename(trimmed);
    setEditing(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 hover:shadow-sm transition mb-2"
    >
      <div className="flex items-center gap-3 min-w-0">
        <span
          {...attributes}
          {...listeners}
          className="cursor-grab shrink-0 text-gray-400 group-hover:text-gray-600"
          title="Arrastar"
        >
          <HiOutlineBars3 />
        </span>

        <button
          type="button"
          onClick={onMarkClosing}
          className={[
            "shrink-0 rounded-md p-1.5",
            isClosing
              ? "text-amber-600 hover:text-amber-700 bg-amber-50 border border-amber-200"
              : "text-gray-400 hover:text-gray-600 hover:bg-gray-100",
          ].join(" ")}
          title="Marcar como fechamento"
        >
          {isClosing ? <HiStar /> : <HiOutlineStar />}
        </button>

        {editing ? (
          <TextInput
            value={localName}
            onChange={(e) => setLocalName(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => e.key === "Enter" && commit()}
            autoFocus
            className="h-9 w-[260px]"
          />
        ) : (
          <button
            className="text-sm font-medium text-gray-900 truncate text-left"
            title="Editar etapa"
            onClick={onOpenInspector}
          >
            {stage.name}
          </button>
        )}

        {/* chips sutis */}
        <span className="ml-2 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-gray-700">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: stage.color || "#141414" }}
          />
          {stage.probability ?? 0}%
        </span>
        {isClosing && (
          <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700">
            <HiCheckCircle /> Fechamento
          </span>
        )}
      </div>

      <div className="flex items-center gap-0.5 shrink-0">
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="text-gray-600 hover:text-gray-800 rounded-md p-1.5 hover:bg-gray-100"
            title="Renomear rápido"
          >
            <HiOutlinePencil />
          </button>
        )}
        <button
          onClick={onOpenInspector}
          className="text-gray-600 hover:text-gray-800 rounded-md p-1.5 hover:bg-gray-100 text-sm"
          title="Abrir painel"
        >
          Detalhes
        </button>
        <button
          onClick={onDelete}
          className="text-red-500 hover:text-red-600 rounded-md p-1.5 hover:bg-red-50"
          title="Excluir etapa"
        >
          <HiTrash />
        </button>
      </div>
    </div>
  );
}

/** Painel lateral com configs espaçosas */
function StageInspector({ open, stage, onClose, onChange, onRename }) {
  if (!open || !stage) return null;
  const bothWinLost = !!stage.isWin && !!stage.isLost;

  const setNum = (k, v) => {
    const n = Number(v);
    if (Number.isFinite(n)) onChange({ [k]: Math.max(0, Math.min(100, n)) });
  };

  return (
    <Modal show={open} onClose={onClose} size="7xl" className="!items-start">
      {/* fake sheet lateral (direita) */}
      <div className="fixed inset-y-0 right-0 w-full sm:w-[560px] lg:w-[680px] bg-white shadow-2xl border-l border-gray-200 overflow-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-gray-500">
              Etapa do funil
            </div>
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {stage.name}
            </h3>
          </div>
          <Button color="light" onClick={onClose}>
            Fechar
          </Button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-10">
          {/* Identificação */}
          <section className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-800">
              Identificação
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
              <div className="md:col-span-12">
                <label className="text-xs font-medium text-gray-600 block mb-1.5">
                  Nome da etapa
                </label>
                <TextInput
                  className="h-11"
                  value={stage.name || ""}
                  onChange={(e) => onRename(e.target.value)}
                  placeholder="Ex.: Qualificação"
                />
              </div>

              <div className="md:col-span-6">
                <label className="text-xs font-medium text-gray-600 block mb-1.5">
                  Probabilidade
                </label>
                <div className="flex items-center gap-2">
                  <TextInput
                    type="number"
                    min={0}
                    max={100}
                    className="h-11 w-28"
                    value={stage.probability ?? 0}
                    onChange={(e) => setNum("probability", e.target.value)}
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
              </div>

              <div className="md:col-span-6">
                <label className="text-xs font-medium text-gray-600 block mb-1.5">
                  Cor
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={stage.color || "#141414"}
                    onChange={(e) => onChange({ color: e.target.value })}
                    className="h-11 w-11 p-0 border rounded"
                  />
                  <span className="text-sm text-gray-600">
                    {stage.color || "#141414"}
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Regras */}
          <section className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-800">Regras</h4>

            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() =>
                  onChange({
                    isWin: !stage.isWin,
                    isLost: stage.isWin ? stage.isLost : false,
                  })
                }
                className={[
                  "px-4 py-2 text-sm flex items-center gap-1.5",
                  stage.isWin
                    ? "bg-emerald-50 text-emerald-700"
                    : "bg-white text-gray-700 hover:bg-gray-50",
                ].join(" ")}
              >
                <HiCheckCircle /> Ganho
              </button>
              <div className="w-px bg-gray-200" />
              <button
                type="button"
                onClick={() =>
                  onChange({
                    isLost: !stage.isLost,
                    isWin: stage.isLost ? stage.isWin : false,
                  })
                }
                className={[
                  "px-4 py-2 text-sm flex items-center gap-1.5",
                  stage.isLost
                    ? "bg-rose-50 text-rose-700"
                    : "bg-white text-gray-700 hover:bg-gray-50",
                ].join(" ")}
              >
                <HiXCircle /> Perdido
              </button>
            </div>

            {bothWinLost && (
              <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded">
                Uma etapa não pode ser “Ganho” e “Perdido” ao mesmo tempo.
              </div>
            )}

            <div className="max-w-sm">
              <label className="text-xs font-medium text-gray-600 block mb-1.5">
                Auto Lead Status
              </label>
              <Select
                className="h-11"
                value={stage.autoLeadStatus || ""}
                onChange={(e) =>
                  onChange({ autoLeadStatus: e.target.value || null })
                }
              >
                {AUTO_LEAD_STATUS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </div>
          </section>

          <div className="h-2" />
        </div>
      </div>
    </Modal>
  );
}

/* ========= Página ========= */
export default function FunnelManager() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useApplication();
  const [selectedFunnel, setSelectedFunnel] = useState(null);

  // Form (modal)
  const [funnelName, setFunnelName] = useState("");
  const [category, setCategory] = useState("");
  const [visibility, setVisibility] = useState("PRIVATE");
  const [teamId, setTeamId] = useState("");
  const [closingStageId, setClosingStageId] = useState("");
  const [startStageId, setStartStageId] = useState("");
  const [visibleUserIds, setVisibleUserIds] = useState([]);
  const [isMetrified, setIsMetrified] = useState(true);

  const [newStage, setNewStage] = useState("");

  // UX extra
  const [search, setSearch] = useState("");
  const [filterVis, setFilterVis] = useState("");
  const [filterCategory, setFilterCategory] = useState("");

  const [confirmId, setConfirmId] = useState(null);

  // errors
  const [funnelNameError, setFunnelNameError] = useState("");
  const [stagesError, setStagesError] = useState("");
  const [visibilityError, setVisibilityError] = useState("");

  const sensors = useSensors(useSensor(PointerSensor));
  const queryClient = useQueryClient();

  // Inspector lateral
  const [inspector, setInspector] = useState({ open: false, key: null });

  const { data: funnels = [], isLoading: loadingFunnels } = useQuery(
    "funnels",
    getFunnels
  );
  const { data: availableUsers = [], isLoading: loadingUsers } = useQuery(
    "funnelUsers",
    getUsersList,
    {
      staleTime: 1000 * 60 * 5,
    }
  );
  const userOptions = useMemo(
    () =>
      availableUsers
        .map((usr) => {
          const id = getUserReferenceId(usr);
          if (!id) return null;
          const labelParts = [];
          if (usr.name) labelParts.push(usr.name);
          if (usr.email) labelParts.push(usr.email);
          return {
            value: String(id),
            label: labelParts.length ? labelParts.join(" — ") : String(id),
          };
        })
        .filter(Boolean),
    [availableUsers]
  );
  const selectedUserOptions = useMemo(() => {
    const ids = new Set(visibleUserIds.map((id) => normalizeId(id)));
    return userOptions.filter((opt) => ids.has(opt.value));
  }, [userOptions, visibleUserIds]);
  const handleVisibleUsersChange = (items) => {
    const nextValues = Array.isArray(items)
      ? items.map((item) => item?.value)
      : [];
    setVisibleUserIds(normalizeIdList(nextValues));
  };
  const categoryOptions = useMemo(() => {
    const set = new Set();
    toArray(funnels).forEach((f) => {
      const value = normalizeCategory(f?.category ?? f?.categoryName ?? "");
      if (value) set.add(value);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [funnels]);

  // Ao voltar do StageFormBuilder com state.openStageId,
  // abrimos automaticamente o modal do funil e o inspector da etapa.
  useEffect(() => {
    const stageIdToOpen = location.state?.openStageId;
    if (!stageIdToOpen || loadingFunnels) return;

    // acha o funil que contém a etapa
    const all = toArray(funnels);
    const found = all.find((f) =>
      toArray(f?.stages).some((s) => String(s.id) === String(stageIdToOpen))
    );
    if (!found) {
      // limpa o state para não tentar de novo
      navigate(".", { replace: true, state: null });
      return;
    }

    // abre o modal de edição do funil
    openEdit(found);
    // abre o inspector dessa etapa específica
    setInspector({ open: true, key: stageIdToOpen });

    // limpa o state (evita reabrir em novas renders/navegações)
    navigate(".", { replace: true, state: null });
  }, [location.state, funnels, loadingFunnels]);

  const saveMut = useMutation(saveFunnel, {
    onSuccess: () => {
      queryClient.invalidateQueries("funnels");
      setSelectedFunnel(null);
      setInspector({ open: false, key: null });
    },
  });
  const delMut = useMutation(deleteFunnel, {
    onSuccess: () => {
      queryClient.invalidateQueries("funnels");
      setConfirmId(null);
    },
  });

  /* ===== open modal (new/edit) ===== */
  const openNew = () => {
    const tmpId = `tmp-${Date.now()}`;
    setSelectedFunnel({ id: "", stages: [], _tmpId: tmpId });
    setFunnelName("");
    setCategory("");
    setVisibility("PRIVATE");
    setClosingStageId(""); // este agora vai guardar clientKey
    setStartStageId("");
    setNewStage("");
    setFunnelNameError("");
    setStagesError("");
    setVisibilityError("");
    const currentUserId = normalizeId(user?.id);
    setVisibleUserIds(currentUserId ? [currentUserId] : []);
    setIsMetrified(true);
  };

  const openEdit = (f) => {
    const stages = toArray(f?.stages).map((s, i) =>
      typeof s === "string"
        ? {
            id: "",
            name: s,
            order: i + 1,
            probability: 0,
            color: "#141414",
            isWin: false,
            isLost: false,
            autoLeadStatus: "",
            requiredFields: [],
            autoTasks: [],
            slaHours: null,
            wipLimit: null,
            webhookOnEnter: null,
          }
        : {
            id: s.id || "",
            name: s.name,
            order: s.order ?? i + 1,
            probability: s.probability ?? 0,
            color: s.color || null,
            isWin: !!s.isWin,
            isLost: !!s.isLost,
            autoLeadStatus: s.autoLeadStatus || "",
            requiredFields: s.requiredFields || [],
            autoTasks: s.autoTasks || [],
            slaHours: s.slaHours ?? null,
            wipLimit: s.wipLimit ?? null,
            webhookOnEnter: s.webhookOnEnter ?? null,
            clientKey: s.clientKey || makeClientKey(), // <-- AQUI
          }
    );

    setSelectedFunnel({ id: f.id, stages: withClientKeys(stages) });
    setFunnelName(f.name || "");
    setCategory(normalizeCategory(f.category ?? f.categoryName ?? ""));
    setVisibility(f.visibility || "PRIVATE");
    setVisibleUserIds(normalizeIdList(gatherFunnelVisibleUserIds(f)));
    setIsMetrified(f.isMetrified !== false);
    // mapeie closingStageId do servidor para o clientKey correspondente
    const closing = toArray(stages).find(
      (s) => (s.id || s.name) === f.closingStageId
    );
    setClosingStageId(closing?.clientKey || "");
    const startStage = toArray(stages).find(
      (s) => (s.id || s.name) === f.startStageId
    );
    setStartStageId(startStage?.clientKey || "");
    setNewStage("");
    setFunnelNameError("");
    setStagesError("");
    setVisibilityError("");
  };

  /* ===== stages ops ===== */
  const setStages = (stages) =>
    setSelectedFunnel((p) => ({ ...(p || {}), stages }));

  const handleAddStage = () => {
    const name = newStage.trim();
    if (!name) return;
    const list = [...(selectedFunnel?.stages || [])];
    list.push({
      id: "",
      clientKey: makeClientKey(), // <-- AQUI
      name,
      order: list.length + 1,
      probability: 0,
      color: "#141414",
      isWin: false,
      isLost: false,
      autoLeadStatus: "",
      requiredFields: [],
      autoTasks: [],
      slaHours: null,
      wipLimit: null,
      webhookOnEnter: null,
    });
    setStages(list);
    setNewStage("");
  };

  const renameStage = (clientKey, newName) => {
    const list = [...(selectedFunnel?.stages || [])];
    setStages(
      list.map((s) => (s.clientKey === clientKey ? { ...s, name: newName } : s))
    );
  };

  const deleteStage = (clientKey) => {
    const list = [...(selectedFunnel?.stages || [])].filter(
      (s) => s.clientKey !== clientKey
    );
    if (closingStageId && closingStageId === clientKey) setClosingStageId("");
    if (startStageId && startStageId === clientKey) setStartStageId("");
    setStages(list.map((s, i) => ({ ...s, order: i + 1 })));
  };

  const markClosing = (clientKey) => setClosingStageId(clientKey);

  const patchStage = (clientKey, patch) =>
    setStages(
      (selectedFunnel?.stages || []).map((s) =>
        s.clientKey === clientKey ? { ...s, ...patch } : s
      )
    );

  const currentStage =
    (selectedFunnel?.stages || []).find((s) => s.clientKey === inspector.key) ||
    null;

  /* ===== drag end ===== */
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const list = [...(selectedFunnel?.stages || [])];
    const oldIdx = list.findIndex((s) => s.clientKey === active.id);
    const newIdx = list.findIndex((s) => s.clientKey === over.id);
    if (oldIdx < 0 || newIdx < 0) return;

    const [moved] = list.splice(oldIdx, 1);
    list.splice(newIdx, 0, moved);
    setStages(list.map((s, i) => ({ ...s, order: i + 1 })));
  };

  /* ===== filtros ===== */
  const filtered = useMemo(() => {
    const list = toArray(funnels);
    const q = search.trim().toLowerCase();
    const v = filterVis.trim();
    const c = filterCategory.trim();
    return list
      .filter((f) =>
        !q
          ? true
          : String(f.name || "")
              .toLowerCase()
              .includes(q)
      )
      .filter((f) =>
        !v ? true : String(f.visibility || "").toUpperCase() === v
      )
      .filter((f) => {
        if (!c) return true;
        const funnelCategory = normalizeCategory(
          f?.category ?? f?.categoryName ?? ""
        );
        if (c === "__none__") return !funnelCategory;
        return funnelCategory === c;
      });
  }, [funnels, search, filterVis, filterCategory]);
  const groupedFunnels = useMemo(() => {
    const groups = new Map();
    filtered.forEach((f) => {
      const value = normalizeCategory(f?.category ?? f?.categoryName ?? "");
      const key = value || "__none__";
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          label: value || "Sem categoria",
          items: [],
        });
      }
      groups.get(key).items.push(f);
    });
    const entries = Array.from(groups.values());
    entries.sort((a, b) => {
      if (a.key === "__none__") return 1;
      if (b.key === "__none__") return -1;
      return a.label.localeCompare(b.label, "pt-BR");
    });
    return entries;
  }, [filtered]);

  /* ===== save ===== */
  const handleSave = () => {
    let hasErr = false;

    if (!funnelName.trim()) {
      setFunnelNameError("O nome do funil é obrigatório.");
      hasErr = true;
    } else setFunnelNameError("");

    const hasStages = (selectedFunnel?.stages || []).length > 0;
    if (!hasStages) {
      setStagesError("Adicione pelo menos uma etapa.");
      hasErr = true;
    } else setStagesError("");

    if (
      closingStageId &&
      !(selectedFunnel?.stages || []).some(
        (s) => s.clientKey === closingStageId
      )
    ) {
      setStagesError("A etapa de fechamento selecionada não existe.");
      hasErr = true;
    }
    if (
      startStageId &&
      !(selectedFunnel?.stages || []).some((s) => s.clientKey === startStageId)
    ) {
      setStagesError("A etapa de início selecionada não existe.");
      hasErr = true;
    }

    const invalidStage = (selectedFunnel?.stages || []).find(
      (s) => s.isWin && s.isLost
    );
    if (invalidStage) {
      setStagesError(
        `A etapa "${invalidStage.name}" está marcada como Ganho e Perdido ao mesmo tempo.`
      );
      hasErr = true;
    }

    if (hasErr) return;

    const currentUserId = normalizeId(user?.id);
    const normalizedVisibleIds =
      visibility === "PRIVATE"
        ? Array.from(
            new Set(
              [...normalizeIdList(visibleUserIds), currentUserId].filter(
                Boolean
              )
            )
          )
        : [];

    const closingStage =
      (selectedFunnel?.stages || []).find(
        (s) => s.clientKey === closingStageId
      ) || null;
    const startStage =
      (selectedFunnel?.stages || []).find(
        (s) => s.clientKey === startStageId
      ) || null;

    const payload = {
      id: selectedFunnel?.id || undefined,
      name: funnelName.trim(),
      category: normalizeCategory(category) || null,
      visibility,
      // mapeie closingStageId (clientKey) para o ID real (se existir) OU mantenha nulo
      closingStageId: closingStage
        ? closingStage.id || closingStage.name || null
        : null,
      // etapa considerada como "negócio iniciado"
      startStageId: startStage ? startStage.id || startStage.name || null : null,
      ownerUserId: visibility === "PRIVATE" ? user?.id ?? null : null,
      visibleUserIds: normalizedVisibleIds,
      stages: (selectedFunnel?.stages || []).map((s, i) => ({
        id: s.id || undefined,
        name: s.name,
        order: s.order ?? i + 1,
        probability: Number.isFinite(+s.probability) ? +s.probability : 0,
        color: s.color || null,
        isWin: !!s.isWin,
        isLost: !!s.isLost,
        autoLeadStatus: s.autoLeadStatus || null,
        requiredFields: Array.isArray(s.requiredFields) ? s.requiredFields : [],
        autoTasks: Array.isArray(s.autoTasks) ? s.autoTasks : [],
        slaHours: s.slaHours ?? null,
        wipLimit: s.wipLimit ?? null,
        webhookOnEnter: s.webhookOnEnter ?? null,
      })),
      isMetrified,
    };

    saveMut.mutate(payload);
  };

  /* ===== chip de visibilidade (flat) ===== */
  const visibilityChip = (f) => {
    const v = (f.visibility || "PRIVATE").toUpperCase();
    if (v === "PUBLIC")
      return (
        <span className="px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 text-[11px]">
          Público
        </span>
      );

    return (
      <span className="px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-gray-700 text-[11px]">
        Privado
      </span>
    );
  };

  /* ===== UI ===== */
  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="px-3 md:px-4 py-3">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1 shadow-sm">
                <span className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full bg-gray-900 text-white text-[10px] font-bold leading-none">
                  F
                </span>
                <span className="text-[13px] font-semibold text-gray-900 leading-none">
                  Funis
                </span>
                <Badge color="gray" className="ml-1">
                  {toArray(funnels).length}
                </Badge>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <TextInput
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar funil…"
                  className="pl-9 h-9 min-w-[210px]"
                />
                <HiOutlineFunnel className="absolute left-2 top-2.5 text-gray-400 pointer-events-none" />
              </div>
              <Select
                value={filterVis}
                onChange={(e) => setFilterVis(e.target.value)}
                className="h-9"
              >
                <option value="">Visibilidade: todas</option>
                {VISIBILITY.map((v) => (
                  <option key={v.value} value={v.value}>
                    {v.label}
                  </option>
                ))}
              </Select>
              <Select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="h-9"
              >
                <option value="">Categoria: todas</option>
                {categoryOptions.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
                <option value="__none__">Sem categoria</option>
              </Select>
              <Button className="mt-1" onClick={openNew}>
                <div className="flex items-center">
                  <HiOutlinePlus className="mr-2" /> <span>Novo Funil</span>
                </div>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Lista */}
      {loadingFunnels ? (
        <div className="grid sm:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
            >
              <div className="h-4 w-2/3 rounded bg-gray-100 animate-pulse" />
              <div className="mt-3 space-y-2">
                {[...Array(3)].map((__, r) => (
                  <div
                    key={r}
                    className="h-8 rounded bg-gray-50 animate-pulse"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed rounded-xl border-gray-300 bg-gray-50 text-center">
          <HiOutlineBars3 className="text-5xl text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700">
            Nenhum funil encontrado
          </h3>
          <p className="text-sm text-gray-500 mt-1 mb-4">
            Crie seu primeiro funil para organizar seus processos.
          </p>
          <Button onClick={openNew}>
            <HiOutlinePlus className="mr-2" /> Criar Funil
          </Button>
        </div>
      ) : (
        <div className="space-y-5">
          {groupedFunnels.map((group) => (
            <div key={group.key} className="space-y-2">
              <div className="flex items-center gap-2 px-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  {group.label}
                </span>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-gray-600">
                  {group.items.length}
                </span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {group.items.map((funnel) => (
                  <div
                    key={funnel.id}
                    className="relative rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow transition"
                  >
                    {/* Cabeçalho do card */}
                    <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h2 className="text-base font-semibold leading-tight text-gray-900 truncate">
                            {funnel.name}
                          </h2>
                          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-gray-600">
                            {visibilityChip(funnel)}
                            <span
                              className={[
                                "px-2 py-0.5 rounded-full border text-[11px]",
                                funnel.isMetrified === false
                                  ? "bg-amber-50 border-amber-200 text-amber-700"
                                  : "bg-emerald-50 border-emerald-200 text-emerald-700",
                              ].join(" ")}
                            >
                              {funnel.isMetrified === false
                                ? "Sem métricas"
                                : "Metrificado"}
                            </span>
                            <span className="px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200">
                              Categoria:{" "}
                              {normalizeCategory(
                                funnel.category ?? funnel.categoryName ?? ""
                              ) || "Sem categoria"}
                            </span>
                            {funnel.closingStageId ? (
                              <span className="px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200">
                                Fechamento:{" "}
                                {funnel.stages?.find(
                                  (s) => (s.id || s.name) === funnel.closingStageId
                                )?.name || "—"}
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200">
                                Fechamento não definido
                              </span>
                            )}
                            {funnel.startStageId ? (
                              <span className="px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200">
                                Início:{" "}
                                {funnel.stages?.find(
                                  (s) => (s.id || s.name) === funnel.startStageId
                                )?.name || "—"}
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200">
                                Início não definido
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            size="xs"
                            color="light"
                            onClick={() => openEdit(funnel)}
                          >
                            <div className="flex items-center">
                              <HiOutlinePencil className="mr-1" />{" "}
                              <span>Editar</span>
                            </div>
                          </Button>
                          <Button
                            size="xs"
                            color="light"
                            onClick={() => setConfirmId(funnel.id)}
                          >
                            <div className="flex items-center">
                              <HiTrash className="mr-1" /> <span>Excluir</span>
                            </div>
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Corpo (lista de etapas simples) */}
                    <div className="p-4">
                      <div className="mb-2 text-xs text-gray-500">
                        {toArray(funnel?.stages)?.length || 0} etapa(s)
                      </div>
                      <div className="space-y-2">
                        {toArray(funnel?.stages)?.map((stage, idx) => (
                          <div
                            key={stage.id || stage.name || idx}
                            className="flex items-center gap-2 text-sm text-gray-700 bg-white rounded-lg px-3 py-2 border border-gray-200"
                          >
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400" />
                            <span className="truncate">
                              {stage.name || stage}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal CRUD do Funil */}
      <Modal
        show={!!selectedFunnel}
        onClose={() => setSelectedFunnel(null)}
        size="4xl"
      >
        <Modal.Header className="py-2">
          <span className="text-sm md:text-base font-bold text-gray-800 mt-1">
            {selectedFunnel?.id ? "Editar Funil" : "Novo Funil"}
          </span>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-6">
            {/* Nome */}
            <div className="rounded-lg border border-gray-200 p-4 bg-white">
              <label className="text-sm font-medium text-gray-700 block mb-1">
                Nome do Funil *
              </label>
              <TextInput
                value={funnelName}
                onChange={(e) => setFunnelName(e.target.value)}
                placeholder="Ex: Pós-venda"
                color={funnelNameError ? "failure" : undefined}
                helperText={funnelNameError && <span>{funnelNameError}</span>}
              />
              <div className="mt-4">
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Categoria
                </label>
                <TextInput
                  list="funnel-categories"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="Ex: Comercial, Pós-venda"
                />
                <datalist id="funnel-categories">
                  {categoryOptions.map((cat) => (
                    <option key={cat} value={cat} />
                  ))}
                </datalist>
                <p className="text-xs text-gray-500 mt-1">
                  Ajuda a separar os funis por área ou time.
                </p>
              </div>
            </div>

            {/* Visibilidade */}
            <div className="rounded-lg border border-gray-200 p-4 bg-white">
              <div className="grid  gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">
                    Visibilidade *
                  </label>
                  <Select
                    value={visibility}
                    onChange={(e) => setVisibility(e.target.value)}
                    color={visibilityError ? "failure" : undefined}
                  >
                    {VISIBILITY.map((v) => (
                      <option key={v.value} value={v.value}>
                        {v.label}
                      </option>
                    ))}
                  </Select>
                  {visibility === "PRIVATE" && (
                    <div className="mt-4">
                      <label className="text-sm font-medium text-gray-700 block mb-1">
                        Usuários com acesso
                      </label>
                      <ReactSelect
                        isMulti
                        options={userOptions}
                        value={selectedUserOptions}
                        onChange={handleVisibleUsersChange}
                        styles={reactSelectStyles}
                        placeholder="Selecione usuários..."
                        closeMenuOnSelect={false}
                        isLoading={loadingUsers}
                        isDisabled={loadingUsers}
                        menuPortalTarget={
                          typeof document !== "undefined" ? document.body : null
                        }
                        menuPosition="fixed"
                        classNamePrefix="rs"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Inclui você automaticamente; selecione outros
                        colaboradores para conceder acesso.
                      </p>
                    </div>
                  )}
                  {visibilityError && (
                    <p className="text-xs text-red-600 mt-1">
                      {visibilityError}
                    </p>
                  )}
                  <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                    <label className="flex items-start gap-3 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={isMetrified}
                        onChange={(e) => setIsMetrified(e.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-2 focus:ring-gray-900"
                      />
                      <span>
                        <span className="font-medium">Funil metrificado</span>
                        <span className="block text-xs text-gray-500">
                          Se desativado, este funil não entra nas métricas do
                          dashboard.
                        </span>
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Etapas */}
            <div className="rounded-lg border border-gray-200 p-4 bg-white">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-gray-700">
                  Etapas do Funil
                </h4>
                <span className="text-xs text-gray-500">
                  Arraste para reordenar • clique em “Detalhes” para editar
                </span>
              </div>

              {selectedFunnel?.stages?.length > 0 && (
                <div className="bg-gray-50 rounded-md p-3 border border-gray-200 max-h-[360px] overflow-y-auto">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={onDragEnd}
                  >
                    <SortableContext
                      items={(selectedFunnel?.stages || []).map(
                        (s) => s.clientKey
                      )} // <-- AQUI
                      strategy={verticalListSortingStrategy}
                    >
                      {(selectedFunnel?.stages || []).map((stage) => (
                        <SortableStageRow
                          key={stage.clientKey}
                          id={stage.clientKey} // <-- AQUI
                          stage={stage}
                          isClosing={closingStageId === stage.clientKey}
                          onRename={(newName) =>
                            renameStage(stage.clientKey, newName)
                          }
                          onMarkClosing={() => markClosing(stage.clientKey)}
                          onDelete={() => deleteStage(stage.clientKey)}
                          onOpenInspector={() =>
                            setInspector({ open: true, key: stage.clientKey })
                          }
                        />
                      ))}
                    </SortableContext>
                  </DndContext>

                  {stagesError && (
                    <p className="text-sm text-red-500 mt-2">{stagesError}</p>
                  )}
                </div>
              )}

              <div className="flex gap-2 mt-3">
                <TextInput
                  value={newStage}
                  onChange={(e) => setNewStage(e.target.value)}
                  placeholder="Nova etapa"
                  className="flex-1"
                  onKeyDown={(e) => e.key === "Enter" && handleAddStage()}
                />
                <Button size="md" onClick={handleAddStage}>
                  Adicionar
                </Button>
              </div>

              {/* Fechamento */}
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">
                    Etapa de fechamento (opcional)
                  </label>
                  <Select
                    value={closingStageId || ""}
                    onChange={(e) => setClosingStageId(e.target.value || "")}
                  >
                    <option value="">— Não definido —</option>
                    {(selectedFunnel?.stages || []).map((s) => (
                      <option key={s.clientKey} value={s.clientKey}>
                        {s.name}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">
                    Etapa de início (negócio iniciado)
                  </label>
                  <Select
                    value={startStageId || ""}
                    onChange={(e) => setStartStageId(e.target.value || "")}
                  >
                    <option value="">— Não definido —</option>
                    {(selectedFunnel?.stages || []).map((s) => (
                      <option key={s.clientKey} value={s.clientKey}>
                        {s.name}
                      </option>
                    ))}
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Contabiliza negócios iniciados quando o card estiver nesta
                    etapa.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="flex justify-end pt-2 pb-3">
          <Button
            color="gray"
            onClick={() => setSelectedFunnel(null)}
            className="w-full sm:w-auto"
            disabled={saveMut.isLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            className="w-full sm:w-auto"
            isProcessing={saveMut.isLoading}
            disabled={saveMut.isLoading}
          >
            {saveMut.isLoading ? "Salvando..." : "Salvar Funil"}
          </Button>
        </Modal.Footer>

        {/* Painel lateral de etapa (master–detail) */}
        <StageInspector
          open={inspector.open}
          stage={currentStage} // agora sempre encontrado por clientKey
          onClose={() => setInspector({ open: false, key: null })}
          onRename={(name) => renameStage(inspector.key, name)}
          onChange={(patch) => patchStage(inspector.key, patch)}
        />
      </Modal>

      {/* Modal de confirmação de exclusão */}
      <Modal show={!!confirmId} onClose={() => setConfirmId(null)}>
        <Modal.Header>Excluir funil</Modal.Header>
        <Modal.Body>
          <p className="text-sm text-gray-600">
            Tem certeza que deseja excluir este funil? Esta ação não pode ser
            desfeita.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button color="gray" onClick={() => setConfirmId(null)}>
            Cancelar
          </Button>
          <Button
            color="failure"
            onClick={() => delMut.mutate(confirmId)}
            disabled={delMut.isLoading}
          >
            {delMut.isLoading ? (
              <>
                <Spinner size="sm" className="mr-2" /> Excluindo…
              </>
            ) : (
              "Excluir"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
